import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { agencyService } from '../../services/agencyService';
import { toast } from 'react-toastify';
import {
  ArrowLeftIcon,
  PencilIcon,
  TrashIcon,
  PhoneIcon,
  EnvelopeIcon,
  GlobeAltIcon,
  IdentificationIcon,
  MapPinIcon,
  CalendarIcon,
  CurrencyDollarIcon,
  DocumentTextIcon
} from '@heroicons/react/24/outline';

const AgencyDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [agency, setAgency] = useState(null);
  const [reservations, setReservations] = useState([]);
  const [account, setAccount] = useState(null);
  const [activeTab, setActiveTab] = useState('info');
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    fetchAgencyData();
  }, [id]);

  const fetchAgencyData = async () => {
    setIsLoading(true);
    try {
      const agencyResponse = await agencyService.getAgencyById(id);
      // API yanıtını kontrol et ve doğru şekilde işle
      if (agencyResponse && agencyResponse.agency) {
        setAgency(agencyResponse.agency);
      } else {
        throw new Error('Acente bilgileri alınamadı');
      }

      if (activeTab === 'reservations') {
        fetchReservations();
      } else if (activeTab === 'account') {
        fetchAccount();
      }
    } catch (error) {
      toast.error('Acente bilgileri yüklenirken bir hata oluştu');
      navigate('/agencies');
    } finally {
      setIsLoading(false);
    }
  };

  const fetchReservations = async () => {
    try {
      const reservationsResponse = await agencyService.getAgencyReservations(id, { limit: 10 });
      setReservations(reservationsResponse.data.data.reservations);
    } catch (error) {
      toast.error('Rezervasyon bilgileri yüklenirken bir hata oluştu');
    }
  };

  const fetchAccount = async () => {
    try {
      const accountResponse = await agencyService.getAgencyAccount(id);
      setAccount(accountResponse.data.data);
    } catch (error) {
      toast.error('Hesap bilgileri yüklenirken bir hata oluştu');
    }
  };

  const handleTabChange = (tab) => {
    setActiveTab(tab);

    if (tab === 'reservations' && reservations.length === 0) {
      fetchReservations();
    } else if (tab === 'account' && !account) {
      fetchAccount();
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('Bu acenteyi silmek istediğinizden emin misiniz?')) return;

    try {
      await agencyService.deleteAgency(id);
      toast.success('Acente başarıyla silindi');
      navigate('/agencies');
    } catch (error) {
      toast.error(error.response?.data?.message || 'Acente silinirken bir hata oluştu');
    }
  };

  const handleToggleStatus = async () => {
    if (!agency) return;

    try {
      await agencyService.toggleAgencyStatus(id, !agency.isActive);
      toast.success(`Acente ${agency.isActive ? 'pasif' : 'aktif'} duruma getirildi`);
      fetchAgencyData();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Acente durumu değiştirilirken bir hata oluştu');
    }
  };

  if (isLoading && !agency) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  if (!agency) {
    return (
      <div className="text-center py-12">
        <h3 className="text-lg font-medium text-gray-900">Acente bulunamadı</h3>
        <div className="mt-6">
          <Link
            to="/agencies"
            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
          >
            Acente Listesine Dön
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="pb-5 border-b border-gray-200 sm:flex sm:items-center sm:justify-between">
        <h1 className="text-2xl font-semibold text-gray-900">Acente Detayları</h1>
        <div className="mt-3 sm:mt-0 sm:ml-4">
          <Link
            to="/agencies"
            className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
          >
            <ArrowLeftIcon className="-ml-1 mr-2 h-5 w-5 text-gray-500" aria-hidden="true" />
            Geri Dön
          </Link>
        </div>
      </div>

      <div className="mt-6 bg-white shadow overflow-hidden sm:rounded-lg">
        <div className="px-4 py-5 sm:px-6 flex justify-between items-center">
          <div>
            <h3 className="text-lg leading-6 font-medium text-gray-900">{agency.name}</h3>
            <p className="mt-1 max-w-2xl text-sm text-gray-500">
              {agency.contactPerson} - {agency.email}
            </p>
          </div>
          <div className="flex space-x-3">
            <button
              onClick={handleToggleStatus}
              className={`inline-flex items-center px-3 py-1.5 border border-transparent text-xs font-medium rounded-md ${
                agency.isActive
                  ? 'text-red-700 bg-red-100 hover:bg-red-200'
                  : 'text-green-700 bg-green-100 hover:bg-green-200'
              } focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500`}
            >
              {agency.isActive ? 'Pasif Yap' : 'Aktif Yap'}
            </button>
            <Link
              to={`/agencies/${id}/edit`}
              className="inline-flex items-center px-3 py-1.5 border border-transparent text-xs font-medium rounded-md text-primary-700 bg-primary-100 hover:bg-primary-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
            >
              <PencilIcon className="-ml-0.5 mr-2 h-4 w-4" aria-hidden="true" />
              Düzenle
            </Link>
            <button
              onClick={handleDelete}
              className="inline-flex items-center px-3 py-1.5 border border-transparent text-xs font-medium rounded-md text-red-700 bg-red-100 hover:bg-red-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
            >
              <TrashIcon className="-ml-0.5 mr-2 h-4 w-4" aria-hidden="true" />
              Sil
            </button>
          </div>
        </div>

        {/* Tabs */}
        <div className="border-b border-gray-200">
          <nav className="-mb-px flex" aria-label="Tabs">
            <button
              onClick={() => handleTabChange('info')}
              className={`${
                activeTab === 'info'
                  ? 'border-primary-500 text-primary-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              } w-1/3 py-4 px-1 text-center border-b-2 font-medium text-sm`}
            >
              Genel Bilgiler
            </button>
            <button
              onClick={() => handleTabChange('reservations')}
              className={`${
                activeTab === 'reservations'
                  ? 'border-primary-500 text-primary-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              } w-1/3 py-4 px-1 text-center border-b-2 font-medium text-sm`}
            >
              Rezervasyonlar
            </button>
            <button
              onClick={() => handleTabChange('account')}
              className={`${
                activeTab === 'account'
                  ? 'border-primary-500 text-primary-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              } w-1/3 py-4 px-1 text-center border-b-2 font-medium text-sm`}
            >
              Hesap Bilgileri
            </button>
          </nav>
        </div>

        {/* Tab Content */}
        <div className="px-4 py-5 sm:p-6">
          {activeTab === 'info' && (
            <div className="grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-6">
              <div className="sm:col-span-3">
                <div className="flex items-center">
                  <PhoneIcon className="h-5 w-5 text-gray-400 mr-2" />
                  <span className="text-sm font-medium text-gray-500">Telefon:</span>
                  <span className="ml-2 text-sm text-gray-900">{agency.phone}</span>
                </div>
              </div>
              <div className="sm:col-span-3">
                <div className="flex items-center">
                  <EnvelopeIcon className="h-5 w-5 text-gray-400 mr-2" />
                  <span className="text-sm font-medium text-gray-500">E-posta:</span>
                  <span className="ml-2 text-sm text-gray-900">{agency.email}</span>
                </div>
              </div>
              <div className="sm:col-span-3">
                <div className="flex items-center">
                  <IdentificationIcon className="h-5 w-5 text-gray-400 mr-2" />
                  <span className="text-sm font-medium text-gray-500">Vergi No:</span>
                  <span className="ml-2 text-sm text-gray-900">{agency.taxNumber || '-'}</span>
                </div>
              </div>
              <div className="sm:col-span-3">
                <div className="flex items-center">
                  <IdentificationIcon className="h-5 w-5 text-gray-400 mr-2" />
                  <span className="text-sm font-medium text-gray-500">Vergi Dairesi:</span>
                  <span className="ml-2 text-sm text-gray-900">{agency.taxOffice || '-'}</span>
                </div>
              </div>
              <div className="sm:col-span-3">
                <div className="flex items-center">
                  <GlobeAltIcon className="h-5 w-5 text-gray-400 mr-2" />
                  <span className="text-sm font-medium text-gray-500">Web Sitesi:</span>
                  <span className="ml-2 text-sm text-gray-900">
                    {agency.website ? (
                      <a href={agency.website} target="_blank" rel="noopener noreferrer" className="text-primary-600 hover:text-primary-900">
                        {agency.website}
                      </a>
                    ) : (
                      '-'
                    )}
                  </span>
                </div>
              </div>
              <div className="sm:col-span-3">
                <div className="flex items-center">
                  <CalendarIcon className="h-5 w-5 text-gray-400 mr-2" />
                  <span className="text-sm font-medium text-gray-500">Kayıt Tarihi:</span>
                  <span className="ml-2 text-sm text-gray-900">
                    {new Date(agency.createdAt).toLocaleDateString('tr-TR')}
                  </span>
                </div>
              </div>
              <div className="sm:col-span-6">
                <div className="flex items-start">
                  <MapPinIcon className="h-5 w-5 text-gray-400 mr-2 mt-0.5" />
                  <span className="text-sm font-medium text-gray-500">Adres:</span>
                  <span className="ml-2 text-sm text-gray-900">{agency.address || '-'}</span>
                </div>
              </div>
              {agency.notes && (
                <div className="sm:col-span-6">
                  <div className="flex items-start">
                    <DocumentTextIcon className="h-5 w-5 text-gray-400 mr-2 mt-0.5" />
                    <span className="text-sm font-medium text-gray-500">Notlar:</span>
                    <span className="ml-2 text-sm text-gray-900">{agency.notes}</span>
                  </div>
                </div>
              )}
            </div>
          )}

          {activeTab === 'reservations' && (
            <div>
              {isLoading ? (
                <div className="flex justify-center py-12">
                  <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-primary-600"></div>
                </div>
              ) : reservations.length === 0 ? (
                <div className="text-center py-12">
                  <p className="text-sm text-gray-500">Bu acenteye ait rezervasyon bulunamadı.</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Rezervasyon No
                        </th>
                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Tarih
                        </th>
                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Nereden - Nereye
                        </th>
                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Yolcu Sayısı
                        </th>
                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Fiyat
                        </th>
                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Durum
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {reservations.map((reservation) => (
                        <tr key={reservation.id}>
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                            {reservation.reservationNumber}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            {new Date(reservation.createdAt).toLocaleDateString('tr-TR')}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            {reservation.fromRegion} - {reservation.toRegion}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            {reservation.passengerCount}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            {reservation.price} {reservation.currency}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                              reservation.status === 'confirmed' ? 'bg-green-100 text-green-800' :
                              reservation.status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                              reservation.status === 'cancelled' ? 'bg-red-100 text-red-800' :
                              'bg-gray-100 text-gray-800'
                            }`}>
                              {reservation.status === 'confirmed' ? 'Onaylandı' :
                               reservation.status === 'pending' ? 'Beklemede' :
                               reservation.status === 'cancelled' ? 'İptal Edildi' :
                               reservation.status}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
              <div className="mt-6 text-center">
                <Link
                  to={`/reservations?agencyId=${id}`}
                  className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-primary-700 bg-primary-100 hover:bg-primary-200"
                >
                  Tüm Rezervasyonları Görüntüle
                </Link>
              </div>
            </div>
          )}

          {activeTab === 'account' && (
            <div>
              {isLoading ? (
                <div className="flex justify-center py-12">
                  <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-primary-600"></div>
                </div>
              ) : !account ? (
                <div className="text-center py-12">
                  <p className="text-sm text-gray-500">Bu acenteye ait hesap bilgisi bulunamadı.</p>
                </div>
              ) : (
                <div>
                  <div className="bg-white shadow overflow-hidden sm:rounded-lg mb-6">
                    <div className="px-4 py-5 sm:px-6">
                      <h3 className="text-lg leading-6 font-medium text-gray-900">Hesap Özeti</h3>
                    </div>
                    <div className="border-t border-gray-200 px-4 py-5 sm:p-0">
                      <dl className="sm:divide-y sm:divide-gray-200">
                        <div className="py-4 sm:py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                          <dt className="text-sm font-medium text-gray-500 flex items-center">
                            <CurrencyDollarIcon className="h-5 w-5 text-gray-400 mr-2" />
                            Bakiye
                          </dt>
                          <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                            {account.account.balance} {account.account.currency}
                          </dd>
                        </div>
                        <div className="py-4 sm:py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                          <dt className="text-sm font-medium text-gray-500">Para Birimi</dt>
                          <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                            {account.account.currency}
                          </dd>
                        </div>
                        <div className="py-4 sm:py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                          <dt className="text-sm font-medium text-gray-500">Son Güncelleme</dt>
                          <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                            {new Date(account.account.updatedAt).toLocaleString('tr-TR')}
                          </dd>
                        </div>
                      </dl>
                    </div>
                  </div>

                  <h4 className="text-lg font-medium text-gray-900 mb-4">Son İşlemler</h4>
                  {account.transactions.length === 0 ? (
                    <p className="text-sm text-gray-500">Henüz işlem bulunmuyor.</p>
                  ) : (
                    <div className="overflow-x-auto">
                      <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                          <tr>
                            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                              Tarih
                            </th>
                            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                              İşlem Tipi
                            </th>
                            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                              Tutar
                            </th>
                            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                              Açıklama
                            </th>
                            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                              Referans
                            </th>
                          </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                          {account.transactions.map((transaction) => (
                            <tr key={transaction.id}>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                {new Date(transaction.transactionDate).toLocaleString('tr-TR')}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap">
                                <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                                  transaction.transactionType === 'payment' ? 'bg-green-100 text-green-800' :
                                  transaction.transactionType === 'refund' ? 'bg-yellow-100 text-yellow-800' :
                                  transaction.transactionType === 'charge' ? 'bg-red-100 text-red-800' :
                                  'bg-gray-100 text-gray-800'
                                }`}>
                                  {transaction.transactionType === 'payment' ? 'Ödeme' :
                                   transaction.transactionType === 'refund' ? 'İade' :
                                   transaction.transactionType === 'charge' ? 'Tahsilat' :
                                   transaction.transactionType}
                                </span>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                {transaction.amount} {transaction.currency}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                {transaction.description}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                {transaction.referenceNumber || '-'}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                  <div className="mt-6 text-center">
                    <Link
                      to={`/finance/transactions?entityType=agency&entityId=${id}`}
                      className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-primary-700 bg-primary-100 hover:bg-primary-200"
                    >
                      Tüm İşlemleri Görüntüle
                    </Link>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AgencyDetail;
